import React, { forwardRef } from 'react';
import { TextInput, TextInputProps, View } from 'react-native';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text } from './Text';

interface InputProps extends TextInputProps {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: string;
  rightIcon?: string;
  onRightIconPress?: () => void;
}

const Container = styled.View`
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const Label = styled(Text)`
  margin-bottom: ${({ theme }) => theme.spacing.xs}px;
`;

const InputContainer = styled.View<{ hasError: boolean; isFocused: boolean }>`
  flex-direction: row;
  align-items: center;
  border-width: 1px;
  border-color: ${({ theme, hasError, isFocused }) => {
    if (hasError) return theme.colors.error;
    if (isFocused) return theme.colors.brandPrimary;
    return theme.colors.border;
  }};
  border-radius: ${({ theme }) => theme.radius.sm}px;
  padding-horizontal: ${({ theme }) => theme.spacing.md}px;
  background-color: ${({ theme }) => theme.colors.surface};
  min-height: ${({ theme }) => theme.minTouchTarget}px;
`;

const StyledTextInput = styled.TextInput`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.body.fontSize}px;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding-vertical: ${({ theme }) => theme.spacing.md}px;
`;

const IconWrapper = styled.View<{ position: 'left' | 'right' }>`
  margin-${({ position }) => (position === 'left' ? 'right' : 'left')}: ${({ theme }) =>
    theme.spacing.sm}px;
`;

const HelperText = styled(Text)<{ isError: boolean }>`
  margin-top: ${({ theme }) => theme.spacing.xs}px;
  color: ${({ theme, isError }) => (isError ? theme.colors.error : theme.colors.textSecondary)};
`;

export const Input = forwardRef<TextInput, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      onRightIconPress,
      onFocus,
      onBlur,
      editable = true,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const [isFocused, setIsFocused] = React.useState(false);

    const handleFocus = (e: any) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: any) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <Container>
        {label && (
          <Label variant="bodyMedium" color="textPrimary">
            {label}
          </Label>
        )}
        <InputContainer hasError={!!error} isFocused={isFocused}>
          {leftIcon && (
            <IconWrapper position="left">
              <Icon name={leftIcon} size={20} color={theme.colors.textSecondary} />
            </IconWrapper>
          )}
          <StyledTextInput
            ref={ref}
            onFocus={handleFocus}
            onBlur={handleBlur}
            editable={editable}
            placeholderTextColor={theme.colors.textTertiary}
            {...props}
          />
          {rightIcon && (
            <IconWrapper position="right">
              <Icon
                name={rightIcon}
                size={20}
                color={theme.colors.textSecondary}
                onPress={onRightIconPress}
                suppressHighlighting
              />
            </IconWrapper>
          )}
        </InputContainer>
        {(error || helperText) && (
          <HelperText variant="caption" isError={!!error}>
            {error || helperText}
          </HelperText>
        )}
      </Container>
    );
  }
);

Input.displayName = 'Input';
